import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Mail, MessageCircle } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

const COLORS = ["#FF6414", "#AF52DE", "#34C759", "#FFCC00", "#FF3B30"];
const RANGES = [{ d: 7, l: "7 days" }, { d: 30, l: "30 days" }, { d: 90, l: "90 days" }];

export default function Reports() {
  const [days, setDays] = useState(30);
  const { data } = useQuery({ queryKey: ["sales", days], queryFn: async () => (await api.get(`/reports/sales?days=${days}`)).data });
  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/reports/dashboard")).data });
  const { data: insights } = useQuery({ queryKey: ["insights", days], queryFn: async () => (await api.get(`/reports/insights?days=${days}`)).data });
  const { data: pack } = useQuery({ queryKey: ["reports-pack", days], queryFn: async () => (await api.get(`/reports/pack?days=${days}`)).data });
  const sym = "₹";


  const emailSummary = async () => {
    try { const { data: r } = await api.post("/reports/send-daily"); toast.success(`Summary emailed to ${r.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
  };
  const whatsappSummary = async () => {
    try { const { data: r } = await api.post("/reports/send-whatsapp"); toast.success(`WhatsApp sent to ${r.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "WhatsApp not configured"); }
  };

  if (!data || !dash) return <div className="p-8 text-neutral-400">Loading reports…</div>;

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Reports & Analytics" subtitle="Sales insights and performance">
        <Button variant="outline" onClick={emailSummary} data-testid="email-summary-btn"><Mail className="w-4 h-4 mr-1" /> Email Summary</Button>
        <Button variant="outline" onClick={whatsappSummary} data-testid="whatsapp-summary-btn"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp Recap</Button>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r.d} onClick={() => setDays(r.d)} data-testid={`range-${r.d}`}
              className={`px-3 py-1.5 rounded-full text-sm ${days === r.d ? "bg-[var(--kul-charcoal)] text-white" : "bg-white border text-neutral-600"}`}>{r.l}</button>
          ))}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat label={`Revenue (${days}d)`} value={money(data.total_revenue, sym)} />
        <Stat label={`Orders (${days}d)`} value={data.total_orders} />
        <Stat label="Avg Order Value" value={money(dash.avg_order_value, sym)} />
        <Stat label="Customers" value={dash.total_customers} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="kul-card p-5">
          <h3 className="font-display font-700 text-lg mb-4">Revenue by Order Type</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.by_type} dataKey="value" nameKey="type" cx="50%" cy="50%" outerRadius={100} label={(e) => e.type}>
                {data.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v, sym)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="kul-card p-5">
          <h3 className="font-display font-700 text-lg mb-4">Revenue by Payment Method</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_method}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="method" tickLine={false} axisLine={false} className="capitalize" style={{ fontSize: 12, textTransform: "capitalize" }} />
              <YAxis tickLine={false} axisLine={false} style={{ fontSize: 12 }} width={50} />
              <Tooltip formatter={(v) => money(v, sym)} />
              <Bar dataKey="value" fill="#E85D04" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="kul-card p-5 mt-4">
        <h3 className="font-display font-700 text-lg mb-4">Top Selling Items (30d)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dash.top_items} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
            <XAxis type="number" tickLine={false} axisLine={false} style={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" tickLine={false} axisLine={false} width={140} style={{ fontSize: 12 }} />
            <Tooltip formatter={(v, n) => n === "revenue" ? money(v, sym) : v} />
            <Bar dataKey="qty" fill="#9D4EDD" radius={[0, 6, 6, 0]} name="qty sold" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {insights && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Stat label="GST collected" value={money(insights.gst_collected, sym)} />
            <Stat label="Loyalty redeemed" value={money(insights.loyalty_redeemed, sym)} />
            <Stat label="Unpaid tickets" value={insights.unpaid_orders} />
            <Stat label="Cancel rate" value={`${insights.cancellation_rate}%`} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <div className="kul-card p-5">
              <h3 className="font-display font-700 text-lg mb-4">Revenue by hour</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={insights.hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="hour" tickLine={false} axisLine={false} style={{ fontSize: 10 }} interval={2} />
                  <YAxis tickLine={false} axisLine={false} style={{ fontSize: 12 }} width={40} />
                  <Tooltip formatter={(v) => money(v, sym)} />
                  <Bar dataKey="revenue" fill="#FF6414" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="kul-card p-5">
              <h3 className="font-display font-700 text-lg mb-4">By channel</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={insights.by_channel} dataKey="value" nameKey="channel" cx="50%" cy="50%" outerRadius={90} label={(e) => e.channel}>
                    {(insights.by_channel || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v, sym)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {pack && (
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="kul-card p-5">
            <h3 className="font-display font-700 text-lg mb-4">Staff sales (pack)</h3>
            <div className="space-y-2 text-sm max-h-64 overflow-y-auto">
              {(pack.by_staff || []).map((s) => (
                <div key={s.name} className="flex justify-between border-b border-neutral-100 py-2">
                  <span>{s.name} · {s.orders} orders</span>
                  <span className="font-semibold">{money(s.revenue, sym)}</span>
                </div>
              ))}
              {!pack.by_staff?.length && <p className="text-neutral-400">No staff sales in range.</p>}
            </div>
          </div>
          <div className="kul-card p-5">
            <h3 className="font-display font-700 text-lg mb-4">Top items · tax · tips</h3>
            <div className="grid grid-cols-3 gap-2 text-sm mb-3">
              <div>Tax {money(pack.tax, sym)}</div>
              <div>Discounts {money(pack.discounts, sym)}</div>
              <div>Tips {money(pack.tips, sym)}</div>
            </div>
            <div className="space-y-2 text-sm max-h-52 overflow-y-auto">
              {(pack.top_items || []).slice(0, 12).map((it) => (
                <div key={it.name} className="flex justify-between border-b border-neutral-100 py-1.5">
                  <span className="truncate mr-2">{it.name} ×{it.qty}</span>
                  <span>{money(it.revenue, sym)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="kul-card p-5">
      <div className="text-xs uppercase tracking-[0.1em] text-neutral-500 font-semibold">{label}</div>
      <div className="mt-2 font-display text-2xl font-800 tracking-tight">{value}</div>
    </div>
  );
}
